/**
 * contextFields — les liaisons d'un MODÈLE au contenu qu'il affiche.
 *
 * Une page-modèle (`modelFor`) rend une instance d'objet : ses blocs lisent
 * `~~._context_.<clé>`, que `BuilderBlockMount` injecte dans leur store. Ce
 * module porte la seule chose que cette liaison ne dit pas d'elle-même : à
 * QUEL champ de l'objet elle correspond, et donc si elle est modifiable.
 *
 * Rien n'est déclaré nulle part : `content: ~~._context_.title` EST le contrat
 * d'éditabilité — exactement comme `inputAttributes.placeholder` l'est pour le
 * texte indicatif d'un formulaire (cf. `placeholderFieldOf` dans
 * `expandEditable`). `expandEditable` estampille le chemin
 * (`data-builder-context-field`), et c'est ICI qu'on décide, au moment de
 * l'édition, s'il tombe sur un champ réel du type d'objet courant.
 *
 * L'éditeur (`text`, `rich-text`, `image-drupal`…) vient du `fields[]` du TYPE
 * D'OBJET, jamais du bloc : c'est ce qui laisse deux types différents partager
 * le même bloc de modèle, chacun avec son propre mapping.
 */

/** Préfixe d'un chemin reactive-json pointant l'objet courant. */
export const CONTEXT_PREFIX = '~~._context_.'

/**
 * Le drapeau qu'un CONTENU ouvert dans son modèle pose dans le store du bloc
 * (`BuilderBlockMount`), et que `expandEditable` interroge pour neutraliser les
 * masquages « pas de valeur ⇒ pas d'élément ».
 *
 * POURQUOI. Un bloc de modèle masque ce que le contenu ne porte pas — une
 * catégorie absente ne doit pas laisser une pastille vide chez le visiteur. En
 * ÉDITION, cette même règle retirait de l'écran TOUT ce qui reste à renseigner :
 * l'éditeur ne voyait ni le champ ni où cliquer, et un contenu neuf s'ouvrait
 * sur une page quasi blanche où seul le titre — jamais masqué, lui — offrait son
 * filigrane. Le drapeau rend ces éléments à l'écran, VIDES, donc porteurs du
 * filigrane et cliquables. Il n'existe qu'en édition d'objet : le rendu public
 * masque exactement comme avant, et aucun YAML n'a à le déclarer.
 */
export const CONTEXT_EDIT_FLAG = '_contextEdit_'

/** Le chemin du drapeau, tel qu'une condition reactive-json le lit. */
export const CONTEXT_EDIT_PATH = `~~.${CONTEXT_EDIT_FLAG}`

/**
 * Le chemin d'objet porté par une valeur de rjBuild, ou null.
 * `'~~._context_.cover.alt'` → `'cover.alt'`.
 */
export const contextPathOf = (value) => {
  if (typeof value !== 'string' || !value.startsWith(CONTEXT_PREFIX)) return null
  const path = value.slice(CONTEXT_PREFIX.length).trim()
  return /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*$/.test(path) ? path : null
}

/**
 * Sous-chemins tolérés APRÈS la clé du champ. Un chemin plus profond n'est
 * pas une autre donnée : c'est une facette de la MÊME valeur, que l'éditeur du
 * champ gouverne en entier.
 *
 *  - `rich-text`    → `.processed` est le rendu serveur ; on édite `{value, format}`
 *  - `image-drupal` → `.url` / `.alt` / `.src` appartiennent à l'image
 *  - `term-ref`     → `.name` est l'intitulé du terme, `.tid` son identifiant :
 *    deux facettes de la MÊME référence. C'est par `.name` qu'un bloc affiche
 *    une catégorie (`TermLabel`), donc c'est ce chemin-là qui doit ouvrir
 *    l'éditeur de terme — sinon la catégorie reste à jamais en lecture.
 *
 * Tout le reste (`category.name` d'un `object-ref` : un AUTRE objet) reste en
 * lecture — on n'édite pas un contenu voisin depuis la fiche courante.
 */
const SUBPATHS = {
  'rich-text': ['processed'],
  'image-drupal': ['url', 'alt', 'src'],
  image: ['url'],
  'term-ref': ['name', 'tid', 'label'],
}

/** Le mapping `{cléDeContexte: cléDObjet}` inversé, ou null si passthrough. */
const isMapped = (mapping) => mapping && typeof mapping === 'object' && Object.keys(mapping).length > 0

/**
 * Applique un `mapping: {contextKey: objectKey}` à la donnée d'une instance et
 * renvoie le dict à injecter sous `_context_`. Passthrough si le mapping est
 * absent. Partagé par le rendu public et l'édition d'objet dans un modèle —
 * les deux doivent voir EXACTEMENT le même contexte.
 */
export const applyContextMapping = (data, mapping) => {
  const source = data ?? {}
  if (!isMapped(mapping)) return source
  const out = {}
  for (const [contextKey, objectKey] of Object.entries(mapping)) {
    out[contextKey] = source[objectKey]
  }
  return out
}

/**
 * Résout un chemin de contexte contre le schéma d'un type d'objet.
 *
 * @param {string} path      Chemin sans le préfixe (`title`, `cover.alt`).
 * @param {object} spec      `{ fields: [...], mapping: {contextKey: objectKey} }`
 * @returns {{contextKey: string, objectKey: string, field: object}|null}
 *          null = ce point du modèle n'est lié à aucun champ éditable du type,
 *          donc il reste en lecture (c'est la règle demandée : seuls les
 *          éléments qui MAPPENT un champ sont modifiables).
 */
export const resolveContextField = (path, { fields = [], mapping = null } = {}) => {
  if (typeof path !== 'string' || path === '') return null
  const [contextKey, ...rest] = path.split('.')

  // Un mapping explicite RESTREINT la surface : `buildContext` ne publie que
  // ses clés, donc une clé hors mapping ne désigne aucune donnée.
  let objectKey = contextKey
  if (isMapped(mapping)) {
    if (!(contextKey in mapping)) return null
    objectKey = mapping[contextKey]
  }

  const field = fields.find((f) => f && f.key === objectKey)
  if (!field) return null

  const type = field.type ?? field.editorType
  if (!type) return null

  if (rest.length > 0) {
    const allowed = SUBPATHS[type] ?? []
    if (rest.length > 1 || !allowed.includes(rest[0])) return null
  }

  return { contextKey, objectKey, field }
}

/**
 * Une valeur d'aperçu est-elle ÉCRIVABLE telle quelle dans un contenu ?
 *
 * Un `_context_` de définition sert l'aperçu du canevas : ses valeurs décrivent
 * à quoi le modèle ressemble, pas ce qu'un contenu porte. Deux formes ne
 * survivraient pas à l'enregistrement, et disparaîtraient SANS ERREUR :
 *
 *  - une image sans `mid` (souvent une URL externe d'illustration) :
 *    `OrbitDataEnricher::stripValue()` retire `url`/`src`/`width`… au presave,
 *    il ne resterait que l'`alt` ;
 *  - une référence de terme sans `tid` réel (`tid: 0`) : elle ne désigne aucune
 *    taxonomie, et le miroir d'entités du presave n'a rien à rattacher.
 *
 * Le texte, lui, s'écrit tel quel — c'est l'essentiel de ce qu'un éditeur veut
 * retrouver pré-rempli.
 */
const isUsablePreviewValue = (value) => {
  if (typeof value === 'number') return true
  if (typeof value === 'string') return value.trim() !== ''
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  // Une image : seul le média Drupal survit au presave.
  if ('url' in value || 'src' in value || 'mid' in value) return Number(value.mid) > 0
  // Une référence de terme : seul un tid réel désigne quelque chose.
  if ('tid' in value) return Number(value.tid) > 0
  // Un objet riche (`{value, format}` d'un rich-text) : écrivable tel quel.
  return Object.keys(value).length > 0
}

/**
 * Les valeurs d'APERÇU qu'un modèle affiche, prêtes à pré-remplir un contenu.
 *
 * Un bloc de modèle ne porte AUCUNE donnée propre : il rend le contenu que
 * l'URL désigne (`~~._context_.X`). Ce qu'on voit en ouvrant le modèle dans le
 * builder vient donc du `rjBuild.data._context_` de sa DÉFINITION — des valeurs
 * d'aperçu, écrites là pour que le modèle ne soit pas une page vide.
 *
 * Les reprendre pour un contenu NEUF, c'est lui donner le point de départ que
 * l'éditeur a déjà sous les yeux, plutôt que des champs vides dans une page qui
 * ne montre plus rien. Seules sont retenues les clés qui tombent sur un champ
 * réel du type (même règle d'éditabilité que le reste du modèle) et dont la
 * valeur survivrait à l'enregistrement (cf. `isUsablePreviewValue`).
 *
 * @param {Array<object>} defs
 *   Les définitions des blocs montés par le modèle (`registry` du builder).
 * @param {object} spec
 *   `{ fields, mapping }` du type d'objet — le mapping renomme les clés.
 * @returns {object}
 *   `{cléDeChamp: valeur}`. Premier bloc gagnant : deux blocs qui déclarent le
 *   même champ décrivent le même contenu, le premier rendu fait foi.
 */
export const previewContextValues = (defs, { fields = [], mapping = null } = {}) => {
  const out = {}
  for (const def of Array.isArray(defs) ? defs : []) {
    const preview = def?.rjBuild?.data?._context_
    if (!preview || typeof preview !== 'object' || Array.isArray(preview)) continue
    for (const [contextKey, value] of Object.entries(preview)) {
      const resolved = resolveContextField(contextKey, { fields, mapping })
      if (!resolved || resolved.objectKey in out) continue
      if (!isUsablePreviewValue(value)) continue
      out[resolved.objectKey] = value
    }
  }
  return out
}

export default resolveContextField
